import { NavLink } from 'react-router-dom'
import { useState, useEffect } from 'react'
import {
  LayoutDashboard,
  CheckSquare,
  MessageSquare,
  Menu,
  ClipboardList,
  FileText,
  Boxes,
  Users,
} from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import { useAuth } from '@/hooks/useAuth'
import { messageService } from '@/services'
import { MobileSlideInMenu } from './MobileSlideInMenu'

const navigation = [
  { name: 'Home', href: '/', icon: LayoutDashboard },
  { name: 'Tasks', href: '/tasks', icon: CheckSquare },
  { name: 'Projects', href: '/projects', icon: ClipboardList },
  { name: 'Messages', href: '/messages', icon: MessageSquare },
]

const moreNavigation = [
  { name: 'Workspaces', href: '/workspaces', icon: Boxes },
  { name: 'Documents', href: '/documents', icon: FileText },
  { name: 'Users', href: '/users', icon: Users },
]

function BottomNavigationMore({ onClose }: { onClose: () => void }) {
  return (
    <nav>
      <div className="space-y-1">
        {moreNavigation.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            onClick={onClose}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-4 rounded-xl px-4 py-3 transition-colors active:scale-95',
                isActive
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'text-foreground hover:bg-muted active:bg-muted'
              )
            }
          >
            {({ isActive }) => (
              <>
                <div
                  className={cn(
                    'flex items-center justify-center h-10 w-10 rounded-full transition-colors',
                    isActive ? 'bg-primary text-primary-foreground' : 'bg-muted'
                  )}
                >
                  <item.icon className="h-5 w-5" />
                </div>
                <span className="text-base">{item.name}</span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  )
}

export function BottomNavigation() {
  const { user } = useAuth()
  const [unreadCount, setUnreadCount] = useState(0)
  const [MobileSlideInMenuOpen, setMobileSlideInMenuOpen] = useState(false)

  useEffect(() => {
    const loadUnreadCount = async () => {
      if (!user) return
      try {
        const unreadMessages = await messageService.getUnreadMessages(user.id)
        setUnreadCount(unreadMessages.length)
      } catch (error) {
        console.error('Failed to load unread message count:', error)
      }
    }

    loadUnreadCount()
    // Refresh count every 30 seconds
    const interval = setInterval(loadUnreadCount, 30000)
    return () => clearInterval(interval)
  }, [user])

  return (
    <>
      <MobileSlideInMenu
        id="mobile-bottom-navigation"
        heading="More Options"
        isOpen={MobileSlideInMenuOpen}
        onClose={() => setMobileSlideInMenuOpen(false)}
      >
        <BottomNavigationMore onClose={() => setMobileSlideInMenuOpen(false)} />
      </MobileSlideInMenu>
      <nav
        className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-sm border-t"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="flex items-center justify-around h-16 px-2">
          {navigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              end={item.href === '/'}
              className={({ isActive }) =>
                cn(
                  'flex flex-col items-center justify-center gap-1 flex-1 h-full rounded-lg transition-all duration-200 relative',
                  'active:scale-95',
                  isActive ? 'text-primary' : 'text-muted-foreground active:text-foreground'
                )
              }
            >
              {({ isActive }) => (
                <>
                  <div className="relative">
                    <item.icon
                      className={cn('h-6 w-6 transition-all duration-200', isActive && 'scale-110')}
                    />
                    {item.name === 'Messages' && unreadCount > 0 && (
                      <div className="absolute -top-1 -right-1 h-4 w-4 bg-destructive rounded-full flex items-center justify-center">
                        <span className="text-[10px] text-destructive-foreground font-semibold antialiased">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      </div>
                    )}
                  </div>
                  <span
                    className={cn(
                      'text-[11px] font-medium transition-all duration-200 antialiased',
                      isActive && 'font-semibold'
                    )}
                  >
                    {item.name}
                  </span>
                  {isActive && (
                    <div className="absolute -top-[1px] left-1/2 -translate-x-1/2 w-12 h-[3px] bg-primary rounded-b-full" />
                  )}
                </>
              )}
            </NavLink>
          ))}

          {/* More Button */}
          <button
            onClick={() => setMobileSlideInMenuOpen(!MobileSlideInMenuOpen)}
            className={cn(
              'flex flex-col items-center justify-center gap-1 flex-1 h-full rounded-lg transition-all duration-200 relative',
              'active:scale-95',
              MobileSlideInMenuOpen
                ? 'text-primary'
                : 'text-muted-foreground active:text-foreground'
            )}
          >
            <Menu
              className={cn(
                'h-6 w-6 transition-all duration-200',
                MobileSlideInMenuOpen && 'scale-110'
              )}
            />
            <span
              className={cn(
                'text-[11px] font-medium transition-all duration-200',
                MobileSlideInMenuOpen && 'font-semibold'
              )}
            >
              More
            </span>
            {MobileSlideInMenuOpen && (
              <div className="absolute -top-[1px] left-1/2 -translate-x-1/2 w-12 h-[3px] bg-primary rounded-b-full" />
            )}
          </button>
        </div>
      </nav>
    </>
  )
}
